'use client';

import { useState } from 'react';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import { PortableText } from 'next-sanity';
import { customRichTextComponent } from '@/sanity/sanityPortableText';
import { PageFAQGroup } from '@/sanity/pages/PageFAQTemplate';

const faqAnswerRichText = customRichTextComponent({
  block: {
    h1: ({ children }) => (
      <h1 className="mb-0 text-[4rem] font-semibold leading-[1.15] text-black">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-0 text-[4rem] font-semibold leading-[1.15] text-black">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-0 text-[3rem] font-semibold leading-[1.2] text-black">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-0 text-[2.5rem] font-semibold leading-[1.2] text-black">
        {children}
      </h4>
    ),
  },
  types: {
    cornerList: ({ value }) => (
      <ul className="flex flex-col p-0 list-none gap-[2rem]">
        {value?.items?.map((item: any, index: number) => (
          <li key={index} className="flex items-center p-0 m-0 gap-[1.5rem]">
            <div className="flex-shrink-0  w-[0.75rem] h-[0.75rem] bg-black " />
            <div className="richtext richtext-start prose-p:mb-0 prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/80">
              <PortableText
                value={item.content}
                components={faqAnswerRichText}
              />
            </div>
          </li>
        ))}
      </ul>
    ),
  },
});

interface FAQAccordionBlockProps {
  group: PageFAQGroup;
}

export default function FAQAccordionBlock({ group }: FAQAccordionBlockProps) {
  const [openValue, setOpenValue] = useState<string>('');

  if (!group.items?.length) return null;

  return (
    <div className="flex flex-col gap-[5rem]">
      {group.categoryTitle && (
        <h2 className="mb-0 text-[3.5rem] lg:text-[4.5rem] font-semibold tracking-[-0.105rem] lg:tracking-[-0.135rem] leading-[1.01] text-black">
          {group.categoryTitle}
        </h2>
      )}

      <AccordionPrimitive.Root
        type="single"
        collapsible
        onValueChange={setOpenValue}
        className="flex flex-col"
      >
        {group.items.map((item, index) => {
          const key = item._key || String(index);
          const nextKey = group.items?.[index + 1]?._key ?? String(index + 1);
          const isOpen = openValue === key;
          const nextIsOpen = openValue === nextKey;
          const isLast = index === (group.items?.length ?? 0) - 1;
          const showDivider = !isLast && !isOpen && !nextIsOpen;

          return (
            <AccordionPrimitive.Item
              key={key}
              value={key}
              className="group/item relative data-[state=open]:bg-[#f8f8f8] data-[state=open]:rounded-[0.25rem] transition-colors"
            >
              <AccordionPrimitive.Header>
                <AccordionPrimitive.Trigger className="group/trigger flex w-full items-center gap-[2rem] lg:gap-[2.5rem] p-[4rem] data-[state=open]:pb-[1.5rem] text-left outline-none font-geist transition-[padding-bottom] duration-300 ease-in-out">
                  <div className="flex-shrink-0 flex items-center justify-center w-[3.5rem] h-[3.5rem] bg-black rounded-[0.25rem] group-data-[state=open]/trigger:rounded-[1rem] transition-all duration-200">
                    <svg
                      className="w-[2.3rem] h-[2.3rem] shrink-0"
                      width="19"
                      height="19"
                      viewBox="0 0 19 19"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <line
                        x1="9.5"
                        y1="2"
                        x2="9.5"
                        y2="17"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                        className="group-data-[state=open]/trigger:opacity-0 transition-opacity duration-200"
                      />
                      <line
                        x1="2"
                        y1="9.5"
                        x2="17"
                        y2="9.5"
                        stroke="white"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </div>

                  <p className="mb-0 text-[2.25rem] font-medium group-data-[state=open]/trigger:font-semibold leading-[1.35] tracking-[-0.0675rem]">
                    {item.question}
                  </p>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>

              <AccordionPrimitive.Content className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
                {item.answer && (
                  <div className="pb-[3rem] lg:pb-[4rem] pl-[9.5rem] lg:pl-[10rem] pr-[2.5rem] lg:pr-[4rem]">
                    <div className="richtext richtext-start flex flex-col gap-[2rem] prose-p:mb-0 prose-p:text-[2rem] prose-p:leading-[1.5] prose-p:text-black/80 prose-h2:mb-0 prose-h3:mb-0 prose-h4:mb-0">
                      <PortableText
                        value={item.answer}
                        components={faqAnswerRichText}
                      />
                    </div>
                  </div>
                )}
              </AccordionPrimitive.Content>

              <div
                className={`absolute bottom-0 left-0 right-0 h-px transition-opacity duration-200 ${showDivider ? 'opacity-100' : 'opacity-0'}`}
                style={{
                  backgroundImage:
                    'repeating-linear-gradient(90deg, rgba(0,0,0,0.12) 0px, rgba(0,0,0,0.12) 8px, transparent 8px, transparent 16px)',
                }}
              />
            </AccordionPrimitive.Item>
          );
        })}
      </AccordionPrimitive.Root>
    </div>
  );
}
